import { mapConcurrent } from "../utils/mapConcurrent";
import { privatePools, privatePoolDeploymentBlock } from "./privatePools";
import { ethers } from "ethers";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import { computeNullifier } from "../joinsplit/keys";
import { EVM_CONFIG } from "../config";
import { type FlatJoinSplitV2TreeScanResult } from "./merkleIndex";
import { readBalanceTreeHistory } from "./balanceTreeHistory";
import { readPinnedPoolCall } from "./pinnedPoolReads";
import {
  assertFlatJoinSplitV2Note,
  BN254_SCALAR_FIELD,
  flatTreeKey,
} from "./notes";

const SYNC_INTERFACE = new ethers.Interface([
  "function currentTreeIndex() view returns (uint256)",
  "function merkleRoot() view returns (bytes32)",
  "function nextLeafIndex() view returns (uint256)",
  "function computeTokenHashForTree(uint256 treeIndex,address token,uint8 tokenType,uint256 tokenSubID) view returns (bytes32)",
  "function nullifierUsed(bytes32 nullifier) view returns (bool)",
]);

const DEFAULT_MAX_TREE_SCANS = 64;
const ABSOLUTE_MAX_TREE_SCANS = 256;

interface NoteCandidate {
  stored: StoredJoinSplitNote;
  note: JoinSplitNote;
  commitment: string;
  treeIndex?: number;
  precheckError?: string;
}

export interface FlatJoinSplitV2NoteSyncResult {
  validated: Map<string, StoredJoinSplitNote>;
  spent: Set<string>;
  pending: Set<string>;
  rejected: Map<string, string>;
  /** Finality-policy block used for every contract read and event scan. */
  safeBlock: number;
  /** Canonical hash pin for `safeBlock`, including pre-deployment results. */
  safeBlockHash: string;
}

async function readAt(
  provider: ethers.Provider,
  pool: string,
  name: string,
  args: readonly unknown[],
  blockTag: number,
): Promise<ethers.Result> {
  const data = SYNC_INTERFACE.encodeFunctionData(name, args);
  const raw = await readPinnedPoolCall(
    provider,
    pool,
    data,
    blockTag,
    EVM_CONFIG.CHAIN_ID,
  );
  return SYNC_INTERFACE.decodeFunctionResult(name, raw);
}

function canonicalCommitment(value: unknown): string {
  if (typeof value !== "string" || !ethers.isHexString(value, 32)) {
    throw new Error("note commitment must be exactly 32 bytes");
  }
  const commitment = value.toLowerCase();
  const scalar = BigInt(commitment);
  if (scalar === 0n || scalar >= BN254_SCALAR_FIELD) {
    throw new Error("note commitment is not a canonical nonzero field element");
  }
  return commitment;
}

function diagnosticKey(value: unknown, index: number): string {
  const raw =
    typeof value === "string" ? value.toLowerCase() : String(value ?? "");
  return raw || `invalid-record:${index}`;
}

function addDiagnosticRejection(
  rejected: Map<string, string>,
  key: string,
  index: number,
  message: string,
): void {
  const uniqueKey = rejected.has(key) ? `${key}#record-${index}` : key;
  rejected.set(uniqueKey, message);
}

function precheckCandidate(
  note: JoinSplitNote,
  chainId: bigint,
  pool: string,
): {
  treeIndex?: number;
  error?: string;
} {
  if (note.protocol !== "flat-joinsplit-v2" || note.schemaVersion !== 2) {
    return { error: "note is outside the flat-v2 namespace" };
  }
  // A record from another deployment cannot become a leaf in this pool.
  // Reject its domain before treating missing/unfinalized leaves as pending.
  if (note.chainId !== Number(chainId)) {
    return { error: "note belongs to a different chain" };
  }
  if (
    !note.poolAddress ||
    !ethers.isAddress(note.poolAddress) ||
    ethers.getAddress(note.poolAddress) !== pool
  ) {
    return { error: "note belongs to a different pool" };
  }
  if (
    note.treeIndex === undefined ||
    !Number.isSafeInteger(note.treeIndex) ||
    note.treeIndex < 0
  ) {
    return { error: "note has no canonical tree index" };
  }
  return { treeIndex: note.treeIndex };
}

function candidateErrorSummary(candidates: readonly NoteCandidate[]): string {
  const errors = [
    ...new Set(
      candidates
        .map((candidate) => candidate.precheckError)
        .filter((error): error is string => Boolean(error)),
    ),
  ];
  return (
    errors.join("; ") || "no authenticated candidate matched this commitment"
  );
}

async function requirePinnedBlock(
  provider: ethers.Provider,
  blockNumber: number,
  expectedHash?: string,
): Promise<string> {
  const block = await provider.getBlock(blockNumber);
  if (!block || block.number !== blockNumber || block.hash === null) {
    throw new Error("Flat JoinSplit V2 sync: safe block is unavailable");
  }
  const blockHash = block.hash.toLowerCase();
  if (expectedHash !== undefined && blockHash !== expectedHash) {
    throw new Error(
      "Flat JoinSplit V2 sync: safe block changed during validation",
    );
  }
  return blockHash;
}

/**
 * Converts encrypted mailbox records into authoritative spendable notes.
 * Mailbox leaf, tree, token-domain, and spent metadata are never trusted.
 * Duplicate ciphertexts are authenticated independently; an authenticated
 * candidate always wins over malformed records carrying the same commitment.
 */
type SyncRequest = {
  provider: ethers.Provider;
  spendingKeys: SpendingKeys;
  notes: StoredJoinSplitNote[];
  poolAddress?: string;
  deploymentBlock?: number;
  finalityBlocks?: number;
  chunkSize?: number;
  /** Caps full deployment-to-safe-block scans caused by untrusted tree claims. */
  maxTreeScans?: number;
  /** Internal multi-pool snapshot: all reads use the same observed head. */
  pinnedHead?: number;
  signal?: AbortSignal;
  preparedSnapshot?: object;
};
type SyncSnapshot = {
  network: { chainId: bigint };
  head: number;
  block: number;
  hash: string;
  states?: Map<string, ethers.Result[]>;
};
const preparedSnapshots = new WeakMap<
  object,
  {
    provider: ethers.Provider;
    depth: number;
    started: number;
    value: SyncSnapshot;
  }
>();

/** Start independent public-chain work while encrypted recovery downloads.
 * The opaque, one-use result is bound to this provider and finality policy.
 * It contains no spendability decision; sync still authenticates every note,
 * reads nullifiers and checks the canonical block again before publishing. */
export async function prepareFlatBalanceSnapshot(
  params: Pick<SyncRequest, "provider" | "notes" | "finalityBlocks" | "signal">,
): Promise<object> {
  const started = performance.now();
  const depth =
    params.finalityBlocks ?? EVM_CONFIG.JOINSPLIT_V2.FINALITY_BLOCKS;
  const [network, head] = await Promise.all([
    params.provider.getNetwork(),
    params.provider.getBlockNumber(),
  ]);
  if (
    network.chainId !== BigInt(EVM_CONFIG.CHAIN_ID) ||
    !Number.isSafeInteger(depth) ||
    depth < 0 ||
    !Number.isSafeInteger(head) ||
    head < 0
  )
    throw Error("Invalid balance snapshot");
  // Base and DogeOS both use the configured confirmation depth. Every read is
  // still pinned to this block's hash and revalidated on the next recovery.
  const block = Math.max(0, head - depth),
    hash = await requirePinnedBlock(params.provider, block);
  const states = new Map<string, ethers.Result[]>();
  await Promise.all(
    privatePools()
      .filter(
        (pool) =>
          block >= privatePoolDeploymentBlock(pool) &&
          params.notes.some(
            (n) => n.note.poolAddress?.toLowerCase() === pool.toLowerCase(),
          ),
      )
      .map(async (pool) => {
        if (params.signal?.aborted) throw Error("Balance refresh cancelled");
        const values = await Promise.all(
          ["currentTreeIndex", "merkleRoot", "nextLeafIndex"].map((name) =>
            readAt(params.provider, pool, name, [], block),
          ),
        );
        states.set(pool.toLowerCase(), values);
        const index = Number(values[0][0]),
          count = Number(values[2][0]);
        if (
          Number.isSafeInteger(index) &&
          index >= 0 &&
          Number.isSafeInteger(count) &&
          count >= 0 &&
          params.notes.some(
            (n) =>
              n.note.poolAddress?.toLowerCase() === pool.toLowerCase() &&
              n.note.treeIndex === index,
          )
        ) {
          await readBalanceTreeHistory({
            provider: params.provider as any,
            pool,
            treeIndex: index,
            fromBlock: privatePoolDeploymentBlock(pool),
            toBlock: block,
            expectedToBlockHash: hash,
            expectedRoot: String(values[1][0]),
            expectedNextLeafIndex: count,
          });
        }
      }),
  );
  const token = Object.freeze({});
  preparedSnapshots.set(token, {
    provider: params.provider,
    depth,
    started,
    value: { network, head, block, hash, states },
  });
  return token;
}
export async function synchronizeFlatJoinSplitV2Notes(
  params: SyncRequest,
): Promise<FlatJoinSplitV2NoteSyncResult> {
  const prepared =
    params.preparedSnapshot && preparedSnapshots.get(params.preparedSnapshot);
  if (params.preparedSnapshot)
    preparedSnapshots.delete(params.preparedSnapshot);
  const snapshot =
    prepared &&
    !params.poolAddress &&
    privatePools().length > 1 &&
    prepared.provider === params.provider &&
    prepared.depth ===
      (params.finalityBlocks ?? EVM_CONFIG.JOINSPLIT_V2.FINALITY_BLOCKS) &&
    performance.now() - prepared.started < 10_000
      ? prepared.value
      : undefined;
  return synchronizeAtSnapshot(params, snapshot);
}
async function synchronizeAtSnapshot(
  params: SyncRequest,
  snapshot?: SyncSnapshot,
): Promise<FlatJoinSplitV2NoteSyncResult> {
  const checkCancelled = () => {
    if (params.signal?.aborted)
      throw new DOMException("Cancelled", "AbortError");
  };
  checkCancelled();
  if (!params.poolAddress && privatePools().length > 1) {
    const pools = privatePools();
    const [network, pinnedHead] = await Promise.all([
      snapshot?.network ?? params.provider.getNetwork(),
      snapshot?.head ?? params.provider.getBlockNumber(),
    ]);
    if (network.chainId !== BigInt(EVM_CONFIG.CHAIN_ID))
      throw Error("Flat JoinSplit V2 sync: provider is on the wrong chain");
    const depth =
      params.finalityBlocks ?? EVM_CONFIG.JOINSPLIT_V2.FINALITY_BLOCKS;
    if (
      !Number.isSafeInteger(depth) ||
      depth < 0 ||
      !Number.isSafeInteger(pinnedHead) ||
      pinnedHead < 0
    )
      throw Error("Invalid balance snapshot");
    const safeBlock = snapshot?.block ?? Math.max(0, pinnedHead - depth);
    const hash =
      snapshot?.hash ?? (await requirePinnedBlock(params.provider, safeBlock));
    const pages = await Promise.all(
      pools.map((poolAddress) =>
        synchronizeAtSnapshot(
          {
            ...params,
            poolAddress,
            pinnedHead,
            deploymentBlock: privatePoolDeploymentBlock(poolAddress),
            notes: params.notes.filter(
              (n) =>
                (n.note.poolAddress || "").toLowerCase() ===
                poolAddress.toLowerCase(),
            ),
          },
          {
            network,
            head: pinnedHead,
            block: safeBlock,
            hash,
            states: snapshot?.states,
          },
        ),
      ),
    );
    checkCancelled();
    await requirePinnedBlock(params.provider, safeBlock, hash);
    const first = pages[0];
    if (pages.some((page) => page.safeBlockHash !== first.safeBlockHash))
      throw Error("Network changed during balance refresh. Try again.");
    const rejected = new Map(pages.flatMap((p) => [...p.rejected]));
    params.notes.forEach((n, i) => {
      if (
        !pools.some(
          (p) => p.toLowerCase() === n.note.poolAddress?.toLowerCase(),
        )
      )
        rejected.set(
          diagnosticKey(n.note.commitment, i),
          "note belongs to an unknown pool",
        );
    });
    return {
      ...first,
      validated: new Map(pages.flatMap((p) => [...p.validated])),
      spent: new Set(pages.flatMap((p) => [...p.spent])),
      pending: new Set(pages.flatMap((p) => [...p.pending])),
      rejected,
    };
  }
  const pool = ethers.getAddress(
    params.poolAddress || EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
  );
  const deploymentBlock =
    params.deploymentBlock ?? privatePoolDeploymentBlock(pool);
  if (!Number.isSafeInteger(deploymentBlock) || deploymentBlock < 0) {
    throw new Error("Flat JoinSplit V2 sync: invalid deployment block");
  }
  const finalityBlocks =
    params.finalityBlocks ?? EVM_CONFIG.JOINSPLIT_V2.FINALITY_BLOCKS;
  if (!Number.isSafeInteger(finalityBlocks) || finalityBlocks < 0) {
    throw new Error("Flat JoinSplit V2 sync: invalid finality policy");
  }
  const maxTreeScans = params.maxTreeScans ?? DEFAULT_MAX_TREE_SCANS;
  if (
    !Number.isSafeInteger(maxTreeScans) ||
    maxTreeScans < 1 ||
    maxTreeScans > ABSOLUTE_MAX_TREE_SCANS
  ) {
    throw new Error(
      `Flat JoinSplit V2 sync: maxTreeScans must be in 1..${ABSOLUTE_MAX_TREE_SCANS}`,
    );
  }

  const [network, latestBlock] = await Promise.all([
    snapshot?.network ?? params.provider.getNetwork(),
    snapshot?.head ?? params.pinnedHead ?? params.provider.getBlockNumber(),
  ]);
  const chainId = BigInt(network.chainId);
  if (chainId !== BigInt(EVM_CONFIG.CHAIN_ID)) {
    throw new Error("Flat JoinSplit V2 sync: provider is on the wrong chain");
  }
  if (!Number.isSafeInteger(latestBlock) || latestBlock < 0) {
    throw new Error(
      "Flat JoinSplit V2 sync: provider returned an invalid head",
    );
  }

  // This is the newest block that actually satisfies the configured depth.
  // It may legitimately precede deployment; never promote deployment itself
  // into the finalized set before enough confirmations exist.
  const hasFinalityDepth = latestBlock >= finalityBlocks;
  const safeBlock =
    snapshot?.block ?? Math.max(0, latestBlock - finalityBlocks);
  const safeBlockHash =
    snapshot?.hash ?? (await requirePinnedBlock(params.provider, safeBlock));

  const validated = new Map<string, StoredJoinSplitNote>();
  const spent = new Set<string>();
  const pending = new Set<string>();
  const rejected = new Map<string, string>();
  const candidatesByCommitment = new Map<string, NoteCandidate[]>();

  params.notes.forEach((stored, index) => {
    const note = (stored as StoredJoinSplitNote | undefined)?.note as
      | JoinSplitNote
      | undefined;
    const rawCommitment = note?.commitment;
    let commitment: string;
    try {
      commitment = canonicalCommitment(rawCommitment);
    } catch (error) {
      addDiagnosticRejection(
        rejected,
        diagnosticKey(rawCommitment, index),
        index,
        error instanceof Error ? error.message : String(error),
      );
      return;
    }

    const precheck = precheckCandidate(note, chainId, pool);
    const candidates = candidatesByCommitment.get(commitment) || [];
    candidates.push({
      stored,
      note,
      commitment,
      treeIndex: precheck.treeIndex,
      precheckError: precheck.error,
    });
    candidatesByCommitment.set(commitment, candidates);
  });

  const finalizePendingBeforeDeployment = async () => {
    for (const [commitment, candidates] of candidatesByCommitment) {
      if (candidates.some((candidate) => !candidate.precheckError)) {
        pending.add(commitment);
      } else {
        rejected.set(commitment, candidateErrorSummary(candidates));
      }
    }
    if (!snapshot)
      await requirePinnedBlock(params.provider, safeBlock, safeBlockHash);
    return {
      validated,
      spent,
      pending,
      rejected,
      safeBlock,
      safeBlockHash,
    };
  };

  if (!hasFinalityDepth || safeBlock < deploymentBlock) {
    return finalizePendingBeforeDeployment();
  }

  if (!candidatesByCommitment.size) return finalizePendingBeforeDeployment();
  const [currentTree, currentRoot, currentCount] =
    snapshot?.states?.get(pool.toLowerCase()) ??
    (await Promise.all([
      readAt(params.provider, pool, "currentTreeIndex", [], safeBlock),
      readAt(params.provider, pool, "merkleRoot", [], safeBlock),
      readAt(params.provider, pool, "nextLeafIndex", [], safeBlock),
    ]));
  const currentTreeRaw = BigInt(currentTree[0]);
  if (currentTreeRaw > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error(
      "Flat JoinSplit V2 sync: current tree index is not canonical",
    );
  }
  const currentTreeIndex = Number(currentTreeRaw);

  const requestedTreeIndexes = new Set<number>();
  for (const candidates of candidatesByCommitment.values()) {
    for (const candidate of candidates) {
      if (
        !candidate.precheckError &&
        candidate.treeIndex !== undefined &&
        candidate.treeIndex <= currentTreeIndex
      ) {
        requestedTreeIndexes.add(candidate.treeIndex);
      }
    }
  }
  const selectedTreeIndexes = [...requestedTreeIndexes]
    .sort((left, right) => {
      if (left === currentTreeIndex) return -1;
      if (right === currentTreeIndex) return 1;
      return right - left;
    })
    .slice(0, maxTreeScans);
  const selectedTreeSet = new Set(selectedTreeIndexes);

  let expectedCurrentRoot: string | undefined;
  let expectedCurrentNextLeafIndex: number | undefined;
  if (selectedTreeSet.has(currentTreeIndex)) {
    expectedCurrentRoot = String(currentRoot[0]);
    const nextLeafRaw = BigInt(currentCount[0]);
    if (nextLeafRaw > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new Error(
        "Flat JoinSplit V2 sync: next leaf index is not canonical",
      );
    }
    expectedCurrentNextLeafIndex = Number(nextLeafRaw);
  }

  // Populate the cache before authenticating individual records. Provider or
  // log-integrity failures remain global failures rather than blaming a note.
  const scanCache = new Map<number, FlatJoinSplitV2TreeScanResult>();
  for (const treeIndex of selectedTreeIndexes) {
    checkCancelled();
    const scan = await readBalanceTreeHistory({
      provider: params.provider as any,
      pool,
      treeIndex,
      fromBlock: deploymentBlock,
      toBlock: safeBlock,
      expectedToBlockHash: safeBlockHash,
      chunkSize: Math.min(params.chunkSize ?? 5_000, 10_000),
      expectedRoot:
        treeIndex === currentTreeIndex ? expectedCurrentRoot : undefined,
      expectedNextLeafIndex:
        treeIndex === currentTreeIndex
          ? expectedCurrentNextLeafIndex
          : undefined,
    });
    scanCache.set(treeIndex, scan);
  }

  const tokenHashCache = new Map<string, Promise<string>>();
  const tokenHashFor = (
    treeIndex: number,
    tokenAddress: string,
  ): Promise<string> => {
    const token = ethers.getAddress(tokenAddress);
    const key = `${treeIndex}:${token.toLowerCase()}`;
    let result = tokenHashCache.get(key);
    if (!result) {
      result = readAt(
        params.provider,
        pool,
        "computeTokenHashForTree",
        [treeIndex, token, 0, 0n],
        safeBlock,
      ).then((decoded) => String(decoded[0]));
      tokenHashCache.set(key, result);
    }
    return result;
  };

  const nullifierStateCache = new Map<string, Promise<boolean>>();
  const nullifierState = (nullifier: string): Promise<boolean> => {
    const key = nullifier.toLowerCase();
    let result = nullifierStateCache.get(key);
    if (!result) {
      result = readAt(
        params.provider,
        pool,
        "nullifierUsed",
        [key],
        safeBlock,
      ).then((decoded) => Boolean(decoded[0]));
      nullifierStateCache.set(key, result);
    }
    return result;
  };

  await mapConcurrent(
    [...candidatesByCommitment],
    32,
    async ([commitment, candidates]) => {
      checkCancelled();
      let authenticated: StoredJoinSplitNote | undefined;
      let authenticatedIsSpent = false;
      let sawPendingCandidate = false;
      const authenticationErrors: string[] = [];

      for (const candidate of candidates) {
        checkCancelled();
        if (candidate.precheckError || candidate.treeIndex === undefined) {
          authenticationErrors.push(
            candidate.precheckError || "note has no canonical tree index",
          );
          continue;
        }
        const treeIndex = candidate.treeIndex;
        if (treeIndex > currentTreeIndex || !selectedTreeSet.has(treeIndex)) {
          sawPendingCandidate = true;
          continue;
        }
        const scan = scanCache.get(treeIndex);
        if (!scan) {
          sawPendingCandidate = true;
          continue;
        }
        const location = scan.tree.locate(commitment);
        if (!location) {
          sawPendingCandidate = true;
          continue;
        }

        let tokenAddress: string;
        try {
          if (
            !candidate.note.tokenData ||
            !ethers.isAddress(candidate.note.tokenData.tokenAddress)
          ) {
            throw new Error("note token address is missing");
          }
          tokenAddress = ethers.getAddress(
            candidate.note.tokenData.tokenAddress,
          );
        } catch (error) {
          authenticationErrors.push(
            error instanceof Error ? error.message : String(error),
          );
          continue;
        }

        // These are canonical pool reads. A provider failure aborts the sync;
        // it must not turn an otherwise valid note into a persistent rejection.
        const expectedTokenHash = await tokenHashFor(treeIndex, tokenAddress);
        const authoritative: JoinSplitNote = {
          ...candidate.note,
          commitment,
          tokenData: {
            ...candidate.note.tokenData!,
            tokenAddress,
          },
          leafIndex: location.leafIndex,
          treeIndex,
          treeKey: flatTreeKey(chainId, pool, treeIndex),
          merkleRoot: scan.root,
        };
        try {
          await assertFlatJoinSplitV2Note({
            note: authoritative,
            chainId,
            poolAddress: pool,
            tokenAddress,
            expectedTokenHash,
            expectedMpk: params.spendingKeys.mpk,
          });
        } catch (error) {
          authenticationErrors.push(
            error instanceof Error ? error.message : String(error),
          );
          continue;
        }

        const nullifier = await computeNullifier(
          params.spendingKeys.nullifyingKey,
          authoritative.tokenHash,
          authoritative.leafIndex,
        );
        authenticatedIsSpent = await nullifierState(nullifier);
        authenticated = {
          ...candidate.stored,
          note: authoritative,
          spent: authenticatedIsSpent,
        };
        // An authenticated candidate wins over every malformed/pending duplicate.
        break;
      }

      if (authenticated) {
        if (authenticatedIsSpent) {
          spent.add(commitment);
        } else {
          validated.set(commitment, { ...authenticated, spent: false });
        }
      } else if (sawPendingCandidate) {
        pending.add(commitment);
      } else {
        rejected.set(
          commitment,
          [...new Set(authenticationErrors)].join("; ") ||
            candidateErrorSummary(candidates),
        );
      }
    },
  );

  checkCancelled();
  if (!snapshot)
    await requirePinnedBlock(params.provider, safeBlock, safeBlockHash);
  return {
    validated,
    spent,
    pending,
    rejected,
    safeBlock,
    safeBlockHash,
  };
}
